import type { MenuProps } from 'antd';
import { Menu, Popover } from 'antd';
import { useNavigate } from 'react-router-dom';
import Paths from '@/configs/router/paths';
import { useSessionContext } from '@/contexts/session/context';
import Dialog from '@/components/dialog';
import ProfileIcon from '@/assets/images/personalMenu/profile.svg?react';
import QRCodeIcon from '@/assets/images/personalMenu/qrcode.svg?react';
import SettingsIcon from '@/assets/images/personalMenu/settings.svg?react';
import LogoutIcon from '@/assets/images/personalMenu/logout.svg?react';
import { useShareQrCodeModal } from '@/components/qr-code/share-qr-code-modal/useShareQrCodeModal';
import { userApis } from '@/configs/apis/user';
import { useChatContext } from '@/contexts/chat/context';
import useEditProfileFormModal from '../../../profileCenter/components/editProfileFormModal/useEditProfileFormModal';
import styles from './index.module.scss';
import EditIcon from './images/edit.svg?react';
import IDIcon from './images/id.svg?react';

const PersonalCenter = ({ children }: { children: React.ReactNode }) => {
  const {
    logout,
    session: { user },
    updateUser,
    setShowProfileInfo,
  } = useSessionContext();
  const { updateChats } = useChatContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [messageApi, contextHolder] = message.useMessage();

  const {
    formModalContext,
    setShow,
    form: usernameForm,
  } = useEditProfileFormModal({
    title: 'Edit Username',
    formItems: [
      {
        formItem: {
          name: 'username',
          rules: [{ required: true, message: 'Please enter your username' }],
          extra:
            'Username: 6-15 characters (letters, numbers, _, or symbols only) UsernameID: One-time change allowed (unique ID)',
        },
        item: <Input placeholder="Enter your Username" showCount maxLength={15} />,
      },
    ],
    onFinish: async (values) => {
      const res = await userApis.usernameCheck({ username: values.username });
      if (res.code === 1) {
        await userApis.usernameUpdate({ username: values.username });
        await updateUser();
        updateChats([{ chat_id: `P_${user!.userId}`, username: values.username }]);
        setShow(false);
      }
    },
    onCancel: () => {
      usernameForm.resetFields();
    },
    onFinishText: 'Save',
    onCancelText: 'Cancel',
  });

  const { setOpen, shareQrCodeModal, groupShareLink } = useShareQrCodeModal({
    messageApi,
    apiParam: {
      user_id: user?.userId,
    },
  });

  useEffect(() => {
    groupShareLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items: MenuProps['items'] = [
    // {
    //   type: 'divider',
    // },
    {
      key: 'profile',
      label: 'Profile',
      icon: <ProfileIcon />,
      onClick: () => {
        setShowProfileInfo({ show: true, info: user! });
      },
    },
    {
      key: 'qrcode',
      label: 'QR Code',
      icon: <QRCodeIcon />,
      onClick: () => {
        setOpen(true);
      },
    },
    {
      key: Paths.SETTINGS_PATH,
      label: 'Settings',
      icon: <SettingsIcon />,
      onClick: () => {
        navigate(Paths.SETTINGS_NOTIFICATIONS);
      },
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutIcon />,
      onClick: () => {
        Dialog({
          centered: true,
          title: <div>Log out?</div>,
          confirmText: 'Logout',
          content:
            'Logout will not delete any data. You can still log in with this account.',
          onConfirm: () => {
            logout();
          },
        });
      },
    },
  ];

  const handleClick: MenuProps['onClick'] = (e) => {
    if (['qrcode', 'profile', 'logout'].includes(e.key)) {
      return;
    }
    setSelectedKeys([e.key]);
  };

  useEffect(() => {
    setSelectedKeys([location.pathname.split('/').slice(0, 3).join('/')]);
  }, [location.pathname]);

  return (
    <>
      <Popover
        placement="rightTop"
        arrow={false}
        classNames={{ root: styles.popoverWrapper }}
        content={
          <div className={styles.content}>
            <div className={styles.userInfo}>
              <MyAvatar
                size="large"
                className={styles.avatar}
                src={user?.avatar as string}
                showProfileClick={false}
              ></MyAvatar>
              <div className={styles.info}>
                <div className={styles.top}>
                  <div className={styles.name} title={user?.name}>
                    {user?.name}
                  </div>
                  <EditIcon
                    className="pointer"
                    onClick={() => {
                      usernameForm.setFieldsValue({ username: user?.username });
                      setShow(true);
                    }}
                  />
                </div>
                <div className={styles.bottom}>
                  <div className={styles.id}>@{user?.username}</div>
                  <div className={styles.label}>
                    <IDIcon />
                  </div>
                </div>
              </div>
            </div>
            <Menu items={items} selectedKeys={selectedKeys} onClick={handleClick} />
          </div>
        }
      >
        {children}
      </Popover>
      {contextHolder}
      {formModalContext}
      {shareQrCodeModal({
        headerAvatar: user?.avatar ?? '',
        headerName: user?.name ?? '',
        shortName: user?.username ?? '',
      })}
    </>
  );
};

export default PersonalCenter;
